import {
  CircularProgress,
  Paper,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { useOktaAuth } from "@okta/okta-react";
import { useContext, useEffect, useState } from "react";
import { UserModuleContext } from "../../context/UserModuleContext";

const WSU_RED = "#A60F2D";

export const ADGroups = () => {
  const [state, setState] = useState({ isLoaded: false, data: [], error: null });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { authState } = useOktaAuth();
  const { userModule } = useContext(UserModuleContext);

  const networkId = userModule.userData["Network ID"];

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await fetch(
            `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/ad/groups`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: authState.accessToken.accessToken,
              },
              body: JSON.stringify({
                networkId,
                callerId: authState.accessToken.claims.sub,
              }),
            }
        );

        if (!res.ok) {
          const text = await res.text();
          const errorObj = JSON.parse(text);
          throw new Error(errorObj?.error?.message || "Unknown server error");
        }

        const result = await res.json();
        setState({ isLoaded: true, data: result.groups || [], error: null });
      } catch (error) {
        setState({ isLoaded: true, data: [], error });
      }
    };

    fetchGroups();
  }, [authState, userModule]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(+e.target.value);
    setPage(0);
  };

  if (!state.isLoaded) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
        </Box>
    );
  }

  if (state.error) {
    return (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Failed to load AD groups
            </Typography>
            <Typography variant="body2">{state.error.message}</Typography>
          </Box>
        </TableContainer>
    );
  }

  if (state.data.length === 0) {
    return (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ px: 3, pt: 3, pb: 1 }}>
            <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
              AD Groups
            </Typography>
            <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>
              {networkId}
            </Typography>
          </Box>
          <Box sx={{ px: 3, py: 2 }}>
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              No AD groups found for {networkId}
            </Typography>
          </Box>
        </TableContainer>
    );
  }

  return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>

        {/* ── Section: AD Groups ── */}
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            AD Groups
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>
            {networkId}
          </Typography>
        </Box>

        <Divider sx={{ mx: 3 }} />

        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                    sx={{
                      backgroundColor: "#4D4D4D",
                      color: "white",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      letterSpacing: 0.5,
                    }}
                >
                  Group Name
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.data
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((group) => (
                      <TableRow
                          key={`${networkId}-${group}`}
                          hover
                          sx={{ "&:last-child td": { borderBottom: 0 } }}
                      >
                        <TableCell>
                          <Typography variant="body2">{group}</Typography>
                        </TableCell>
                      </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={state.data.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid #e0e0e0",
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "0.8rem",
              },
            }}
        />
      </TableContainer>
  );
};